
import { Component, Prop, Vue } from "vue-property-decorator"
import { Getter, namespace } from "vuex-class"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import CaseTable from "@/components/CaseList/CaseTable.vue"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import UserImage from "@/components/UserImage.vue"

import { get } from "lodash"
import moment from "moment"
import "@/scripts/array-operations"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { CalculationType } from "@/types/Field"

import { CalculationDataTypeValue, Case, CaseDetails, DataType, dataTypeByFieldType, Field } from "@/types"
import { Team } from "@/types/team"
import { View } from "@/types/views/view"
import { Severity } from "@/types/severity"
import { groupingFunctions } from "@/types/dataType"
import { UserSettings } from "@/types/userSettings"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

interface CaseListField {
	title: string,
	dataType: string | null,
	displayKey?: string | null,
	group?: (record: any, groupByValue: any) => { key: string, label: string, value: any }[],
	fieldDataType?: DataType | null
}

interface CaseListColumn {
	key: string,
	fieldId?: string,
	displayKey?: string,
	label: string,
	dataType?: string | null,
	fieldDataType?: DataType | null,
	rounding?: string,
	listLabel?: string,
	listItemType?: string,
	extensions?: {[key: string]: any}
	dynamicField?: boolean,
}

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"case-table": CaseTable,
		"user-image": UserImage,
		"schedule-popup": () => import("@/components/Config/Schedules/ScheduleInstance.vue")
	}
})
export default class CaseList extends Vue {
	@Prop() private readonly cases!: CaseDetails[] | null;
	@Prop() private readonly activeView!: View | null;
	@Prop({ type: Number, default: 50 }) private readonly perPage!: number;
	@Prop({ type: String, default: "" }) private readonly searchFilter!: string;

	@caseStore.State private readonly severities!: Severity[];

	@Getter("getScreenWidth") private readonly screenWidth!: number;
	@Getter("getTeamsById")	private readonly teamsById!: { [key: string]: Team };
	@Getter("getCaseName") private readonly getCaseName!: string;
	@Getter("getRecordName") private readonly getRecordName!: string;
	@Getter("getUserSettings") private readonly userSettings!: UserSettings;

	@setupStore.State private readonly fields!: Field[];
	@setupStore.Action private readonly loadFieldsIfUnloaded!: () => Promise<void>;

	@setupStore.Getter("getFilterableFields") getFilterableFields!: CaseColumn[];

	private fieldsDictionary = new Map<string, CaseListField>()

	private groupPageNumbers: { [key: string]: number } = {}

	async mounted () {
		await this.loadFieldsIfUnloaded()
	}

	public get columns (): (CaseListColumn | null)[] {
		if (!this.activeView) {
			return [
				{
					key: "caseNumber",
					label: "Case Number"
				},
				{
					key: "title",
					label: "Title"
				},
				{
					key: "templateTitle",
					label: "Template"
				},
				{
					key: "assigneeId",
					label: "Assignee",
					dataType: "user"
				}
			]
		}

		return this.activeView.columns.map(c => {
			if (c.startsWith("fields.")) {
				let fieldId = c.substring(7)

				if (fieldId.endsWith(".label")) {
					fieldId = fieldId.substring(0, fieldId.length - 6)
				}

				const fieldColumn = this.fields.find(field => field.id === fieldId)
				if (fieldColumn) {
					return {
						key: c,
						fieldId: fieldColumn.id,
						label: fieldColumn.title,
						dataType: fieldColumn.dataType,
						fieldDataType: fieldColumn.dataType === "calculated"
							? CalculationDataTypeValue[fieldColumn.extensions!.calculationType as CalculationType]
							: dataTypeByFieldType[fieldColumn.dataType!]
								? dataTypeByFieldType[fieldColumn.dataType!]
								: null,
						extensions: fieldColumn.extensions,
						dynamicField: true,
						listLabel: "label"
					}
				}
			} else {
				const caseColumn = this.getFilterableFields.find(column => column.key === c)
				if (caseColumn) {
					return {
						key: caseColumn.key,
						displayKey: caseColumn.displayKey,
						label: caseColumn.label,
						dataType: caseColumn.dataType,
						fieldDataType: caseColumn.fieldDataType,
						rounding: caseColumn.rounding,
						listLabel: caseColumn.listLabel || "title",
						listItemType: caseColumn.listItemType
					}
				}
			}

			return null
		}).filter(c => !!c)
	}

	public get groupedCases () {
		// Build fields dictionary for quick access using case columns and custom fields
		this.getFilterableFields.forEach((field: CaseColumn) => {
			this.fieldsDictionary.set(field.key, { title: field.label, dataType: field.dataType, displayKey: field.displayKey, group: field.group })
		})
		this.fields.forEach((field) => {
			this.fieldsDictionary.set(`fields.${field.id}`, {
				title: field.title,
				dataType: field.dataType,
				displayKey: field.dataType === "user-role" ? null : "fields." + field.id + ".label",
				fieldDataType: field.dataType === "calculated"
					? CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
					: dataTypeByFieldType[field.dataType!]
						? dataTypeByFieldType[field.dataType!]
						: null
			})
		})

		if (this.cases == null || this.cases.length === 0) {
			return []
		}

		let cases = this.cases

		if (/* this.enableFiltering && */ !!this.searchFilter && /\S/.test(this.searchFilter)) {
			const casesFiltered: CaseDetails[] = []

			this.columns.forEach((viewColumn) => {
				cases.forEach((existingCase) => {
					if (!casesFiltered.some(filteredCase => filteredCase.id === existingCase.id)) {
						if (viewColumn) {
							let caseColumnValue = this.getCaseColumnValue(viewColumn, existingCase)
							if (caseColumnValue) {
								if (caseColumnValue.toLocaleLowerCase().includes(this.searchFilter.toLocaleLowerCase().trim())) {
									casesFiltered.push(existingCase)
								}
							}
						}
					}
				})
			})

			cases = casesFiltered
			this.$emit("filtered-cases-changed", casesFiltered)
		}

		if (this.activeView && this.activeView.sortBy) {
			// Sorting by severity should be done by level not title
			if (this.activeView!.sortBy === "severityTitle") {
				cases.forEach(c => {
					c.severityLevel = this.severityLevel(c.severityTitle)
				})
				cases = cases.sortBy("severityLevel", false)
			} else {
				cases = cases.sortBy(this.activeView.sortBy as keyof Case, false)
			}
		} else {
			cases = cases.sortBy("created", false)
		}

		if (this.activeView && this.activeView.groupBy) {
			if (!this.fieldsDictionary.get(this.activeView.groupBy)) {
				this.activeView.groupBy = null
			}
		}

		if (this.activeView && this.activeView.groupBy) {
			const groupByKeyColumn = this.fieldsDictionary.get(this.activeView.groupBy)
			const groupBy = groupByKeyColumn
				? groupByKeyColumn.displayKey ?? ""
				: this.activeView!.groupBy

			const caseUsers: { [key:string]: any } = {}
			let getGroups: (record: any, groupByValue: any, cache: { [key:string]: any }, groupByOption?: string | null) => { key: string, label: string, value: any }[] =
				groupByKeyColumn && groupByKeyColumn.group
					? groupByKeyColumn.group
					: !!groupByKeyColumn && groupingFunctions.hasOwnProperty(groupByKeyColumn.fieldDataType ?? "")
						? get(groupingFunctions, groupByKeyColumn.fieldDataType ?? "")
						: (record: any, groupByValue: any, _cache, _groupByOption: string) => {
							return [
								{
									key: groupByValue || "not set",
									label: groupByValue || "Not Set",
									value: groupByValue
								}
							]
						}

			const groupLabels: { [key:string]: {
					label: string,
					value: any,
					sortValue?: any
				}
			} = {}
			const caseGroups = cases
				.reduce((group: any, caseItem) => {
					let groupKey = get(caseItem, groupBy)
					if (!groupKey) {
						groupKey = get(caseItem, this.activeView?.groupBy ?? "")
						if (groupKey && groupKey.value !== undefined) {
							groupKey = groupKey.value
						}
					}

					if (this.activeView && this.activeView.groupByDataType === DataType.dateTime) {
						const value = groupKey
						groupKey = groupKey
							? moment(groupKey).format("L")
							: "Not set"
						groupLabels[groupKey] = {
							label: groupKey,
							value,
							sortValue: value
						};
						(group[groupKey] = group[groupKey] || []).push(caseItem)
					} else {
						const itemGroups = getGroups(caseItem, groupKey, caseUsers, this.activeView!.groupByOption)
						itemGroups.forEach(itemGroup => {
							groupLabels[itemGroup.key] = {
								label: itemGroup.label,
								value: itemGroup.value
							};
							(group[itemGroup.key] = group[itemGroup.key] || []).push(caseItem)
						})
					}

					return group
				}, {})

			return Object.keys(caseGroups).map(groupKey => {
				return Vue.observable({
					key: groupKey,
					label: groupLabels[groupKey] ? groupLabels[groupKey].label : groupKey,
					value: groupLabels[groupKey] ? groupLabels[groupKey].value : groupKey,
					sort: this.activeView!.groupBy === "severityId"
						? this.severityLevel(groupKey)
						: groupLabels[groupKey]
							? groupLabels[groupKey].sortValue
								? groupLabels[groupKey].sortValue
								: groupLabels[groupKey].label
							: groupKey,
					cases: caseGroups[groupKey],
					currentPage: this.groupPageNumbers[groupKey] ?? 1
				})
			}).sortBy("sort")
		} else {
			return [Vue.observable({
				label: null,
				cases: cases,
				currentPage: this.groupPageNumbers["{(( _UNGROUPED_ ))}"] ?? 1
			})]
		}
	}

	private get groupColumn () {
		if (this.activeView?.groupBy) {
			return this.fieldsDictionary.get(this.activeView?.groupBy)
		}
		return null
	}

	private severityLevel (severityTitle: string): number {
		const severity = this.severities && this.severities.find(p => p.title === severityTitle)
		if (severity) {
			return 9999 - severity.level
		} else {
			return 0
		}
	}

	// Method to display checkbox value as string dependent on its value
	private displayCheckBoxValue (value: string): string {
		switch (value) {
			case "null" || "false":
				return "No"
			case "undefined":
				return "Not Specified"
			default:
				return "Yes"
		}
	}

	private getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	private getCaseColumnValue (viewColumn: CaseListColumn, existingCase: CaseDetails): string | null {
		let caseColumnValue: string | null = null

		let columnKey = ""

		let columnValid: boolean = this.getFilterableFields.some(caseColumn => {
			if (caseColumn.key === viewColumn.key) {
				columnKey = viewColumn.key
			} else if (caseColumn.displayKey && caseColumn.displayKey === viewColumn.key) {
				columnKey = caseColumn.displayKey
			}

			return !!existingCase[columnKey]
		})

		if (!columnValid) {
			return null
		}

		if (viewColumn.dataType === "date") {
			caseColumnValue = formatDateMixin.methods.displayDateOnlyFromISOString(viewColumn.dynamicField ? existingCase[columnKey]?.value : existingCase[columnKey])
		} else if (viewColumn.dataType === "date-time") {
			caseColumnValue = formatDateMixin.methods.displayDateFromISOString(
				viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey])
		} else if (viewColumn.dataType === "list" || viewColumn.dataType === "list-multiple") {
			caseColumnValue = existingCase[columnKey]?.label ? existingCase[columnKey]?.label
				: viewColumn.displayKey ? existingCase[columnKey]
					: viewColumn.fieldId ? this.getFieldLabel(viewColumn.fieldId, existingCase)
						: (existingCase[columnKey] && Array.isArray(existingCase[columnKey])) ? existingCase[columnKey].map((i: { [x: string]: any; }) => i[viewColumn.listLabel || "title"]).join(", ")
							: viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey]
		} else if (viewColumn.dataType === "dollar" && existingCase[columnKey]) {
			caseColumnValue = viewColumn.dynamicField ? existingCase[columnKey] : existingCase[columnKey]
		} else if (viewColumn.dataType === "checkbox" && existingCase[columnKey]) {
			caseColumnValue = !(viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey]) ? "No" : "Yes"
		} else if (viewColumn.dataType === "calculated") {
			caseColumnValue = viewColumn.dynamicField ? existingCase[columnKey] : existingCase[columnKey]
		} else if (viewColumn.dataType === "list-data") {
			caseColumnValue = existingCase[columnKey].label
		} else if (viewColumn.dataType === "user" && existingCase[columnKey] && existingCase.users) {
			caseColumnValue = existingCase.users.find(u => u.id === existingCase[columnKey])?.name ?? null
		} else if (viewColumn.dataType === "user-name") {
			caseColumnValue = existingCase[columnKey]
		} else if (!viewColumn.dataType || viewColumn.dataType === "text") {
			caseColumnValue = viewColumn.dynamicField ? existingCase[columnKey].value : existingCase[columnKey]
		}

		return caseColumnValue
	}

	private currentPageChanged (group: any, pageNumber: number) {
		switch (group.key) {
			case undefined:
			case null:
				this.groupPageNumbers["{(( _UNGROUPED_ ))}"] = pageNumber
				break
			default:
				this.groupPageNumbers[group.key] = pageNumber
		}

		const groupIndex = this.groupedCases.indexOf(group)

		group.currentPage = pageNumber

		if (groupIndex >= 0) {
			Vue.set(this.groupedCases[groupIndex], "currentPage", pageNumber)
		}
	}
}
